import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PaintBucket } from "lucide-react";
import { euro, nlNum } from "../lib/api";

export const glueBuckets = (m2, g) => (m2 > 0 && g ? Math.max(1, Math.ceil(Math.round(((m2 * g.g_m2) / 1000 / g.kg) * 1e6) / 1e6)) : 0);

function GlueChoice({ g, n, active, onPick }) {
  return (
    <button type="button" onClick={onPick} aria-pressed={active} data-testid={`glue-option-${g.id}`}
      className={`text-left rounded-xl border p-3 bg-white transition-colors ${active ? "border-[#B78B7B] ring-1 ring-[#B78B7B]" : "border-[#E5E0D8] hover:border-[#B78B7B]"}`}>
      {g.tier && <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A5F50]">{g.tier}</p>}
      <p className="text-sm font-medium leading-snug mt-1 line-clamp-2">{g.name}</p>
      <p className="text-xs text-[#6E747B] mt-1">{g.verbruik} · {euro(g.per_kg_cents)}/kg</p>
      <p className="text-sm mt-2"><strong>{euro(g.price_cents)}</strong>{n > 0 && <span className="text-xs text-[#6E747B]"> · {n}× nodig</span>}</p>
    </button>
  );
}

export default function GlueSet({ p, calc, add }) {
  const opts = p.glue?.options || [];
  const trowels = p.glue?.trowels || [];
  const [sel, setSel] = useState(opts[0]?.id);
  const [kam, setKam] = useState(trowels[0]?.id || "");
  useEffect(() => { setSel(opts[0]?.id); setKam(trowels[0]?.id || ""); }, [p.id]); // eslint-disable-line
  const g = opts.find((x) => x.id === sel);
  const t = trowels.find((x) => x.id === kam);
  const net = calc?.area_m2 || 0;
  if (!g) return null;
  const n = glueBuckets(net, g);
  const total = n * g.price_cents + (t ? t.price_cents : 0);
  const addGlue = async () => {
    if (await add(g.id, n, { glue_for: p.id }) === false) return;
    if (t && await add(t.id, 1, { glue_for: p.id }) === false) return;
    toast.success(`${n}× ${g.name}${t ? " en lijmkam" : ""} toegevoegd aan je winkelwagen`);
  };
  return (
    <div className="bg-[#F2E6E0] rounded-2xl p-5 space-y-3" data-testid="glue-set">
      <p className="flex items-center gap-2 font-medium text-sm"><PaintBucket size={16} className="text-[#B78B7B]" />Lijm en lijmkam voor deze plakvloer</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {opts.map((o) => <GlueChoice key={o.id} g={o} n={glueBuckets(net, o)} active={o.id === sel} onPick={() => setSel(o.id)} />)}
      </div>
      {trowels.length > 0 && (
        <select value={kam} onChange={(e) => setKam(e.target.value)} data-testid="glue-trowel-select" className="w-full border border-[#E5E0D8] rounded-lg px-3 py-2.5 text-sm bg-white">
          {trowels.map((o) => <option key={o.id} value={o.id}>{o.name} – {euro(o.price_cents)}</option>)}
          <option value="">Geen lijmkam, die heb ik al</option>
        </select>)}
      {net > 0 ? (
        <>
          <p className="text-sm" data-testid="glue-calc">Voor {nlNum(net, 3)} m² bij maximaal {g.g_m2} g/m² heb je <strong>{n}× {g.kg} kg</strong> nodig ({nlNum((net * g.g_m2) / 1000, 1)} kg) = <strong>{euro(n * g.price_cents)}</strong>.
            {" "}<Link to={`/product/${g.slug}`} className="underline text-xs">Bekijk lijm</Link></p>
          <button onClick={addGlue} data-testid="add-glue-button"
            className="w-full bg-[#34393E] hover:bg-[#B78B7B] text-white rounded-xl py-3 text-sm font-medium transition-colors">
            Lijm{t ? " en kam" : ""} toevoegen · {euro(total)}</button>
          <p className="text-xs text-[#6E747B]" data-testid="glue-substrate-warning">Deze combinatie geldt voor een droge, vlakke, zuigende en voorbereide ondergrond. Laat primer en egalisatie vooraf beoordelen; volg het productblad.</p>
        </>
      ) : <p className="text-xs text-[#6E747B]" data-testid="glue-calc-empty">Vul hierboven je m² in, dan rekenen we de lijm meteen mee.</p>}
    </div>
  );
}
